import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../components/WebsiteLayout';
import Reveal from '../components/Reveal';
import { useLanguage } from '../../../hooks/useLanguage';

type TabKey = 'llm' | 'emb' | 'clf' | 'oas';

interface TabDef {
  key: TabKey;
  label: { en: string; nl: string };
}

const TABS: TabDef[] = [
  { key: 'llm', label: { en: 'Language model', nl: 'Taalmodel' } },
  { key: 'emb', label: { en: 'Embedding search', nl: 'Embedding-zoeken' } },
  { key: 'clf', label: { en: 'Classifier', nl: 'Classificatiemodel' } },
  { key: 'oas', label: { en: 'oasis_0', nl: 'oasis_0' } },
];

// Base coordinates for the constellation nodes. The rAF loop below jitters each
// pair on its own sine phase so the "sticks" flex without detaching.
const CONSTELLATION_BASE: Array<[number, number]> = [
  [100, 140],
  [205, 230],
  [315, 175],
  [150, 360],
  [400, 285],
  [430, 150],
  [285, 395],
  [60, 300],
];

const CONSTELLATION_LINES: Array<{ a: number; b: number; lit?: boolean }> = [
  { a: 0, b: 1, lit: true },
  { a: 1, b: 3 },
  { a: 1, b: 2, lit: true },
  { a: 2, b: 4 },
  { a: 3, b: 6 },
  { a: 6, b: 4 },
  { a: 4, b: 5 },
];

const Models: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  const [activeTab, setActiveTab] = React.useState<TabKey | null>(null);
  const [autoCycling, setAutoCycling] = React.useState(false);
  const tabsRef = React.useRef<Record<TabKey, HTMLButtonElement | null>>({
    llm: null,
    emb: null,
    clf: null,
    oas: null,
  });
  const differsRef = React.useRef<HTMLElement>(null);
  const skyRef = React.useRef<SVGSVGElement>(null);

  const [openClusters, setOpenClusters] = React.useState<Record<string, boolean>>({});
  const toggleCluster = (id: string) =>
    setOpenClusters((prev) => ({ ...prev, [id]: !prev[id] }));

  // Constellation drift: node "breathes" on a small sine path, links track their
  // two endpoints every frame. Disabled under prefers-reduced-motion.
  React.useEffect(() => {
    const svg = skyRef.current;
    if (!svg || typeof window === 'undefined' || !('requestAnimationFrame' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const params = CONSTELLATION_BASE.map((_, i) => ({
      ax: (4 + (i % 3)) * 1.2,
      ay: (4 + ((i + 1) % 3)) * 1.2,
      px: i * 0.9,
      py: i * 1.7 + 0.5,
      sx: 0.3 + 0.05 * (i % 4),
      sy: 0.26 + 0.05 * (i % 3),
    }));

    const dots: Record<string, SVGElement> = {};
    const rings: Record<string, SVGElement> = {};
    const lines: SVGLineElement[] = [];
    svg.querySelectorAll<SVGElement>('[data-i]').forEach((el) => {
      const i = el.getAttribute('data-i');
      if (i !== null) dots[i] = el;
    });
    svg.querySelectorAll<SVGElement>('[data-ring]').forEach((el) => {
      const r = el.getAttribute('data-ring');
      if (r !== null) rings[r] = el;
    });
    svg.querySelectorAll<SVGLineElement>('line[data-a]').forEach((el) => {
      lines.push(el);
    });

    let start: number | null = null;
    let raf = 0;
    const frame = (ts: number) => {
      if (start === null) start = ts;
      const t = (ts - start) / 1000;
      const pos = CONSTELLATION_BASE.map(([bx, by], i) => {
        const p = params[i];
        return [
          bx + p.ax * Math.sin(t * p.sx + p.px),
          by + p.ay * Math.sin(t * p.sy + p.py),
        ] as [number, number];
      });
      for (let i = 0; i < pos.length; i++) {
        const d = dots[i];
        if (d) {
          d.setAttribute('cx', pos[i][0].toFixed(2));
          d.setAttribute('cy', pos[i][1].toFixed(2));
        }
        const r = rings[i];
        if (r) {
          r.setAttribute('cx', pos[i][0].toFixed(2));
          r.setAttribute('cy', pos[i][1].toFixed(2));
        }
      }
      for (const ln of lines) {
        const ai = +ln.getAttribute('data-a')!;
        const bi = +ln.getAttribute('data-b')!;
        ln.setAttribute('x1', pos[ai][0].toFixed(2));
        ln.setAttribute('y1', pos[ai][1].toFixed(2));
        ln.setAttribute('x2', pos[bi][0].toFixed(2));
        ln.setAttribute('y2', pos[bi][1].toFixed(2));
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Chat comparison auto-cycle: start when the section scrolls into view, stop
  // on any user interaction. Reduced-motion goes straight to the oasis_0 tab.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const section = differsRef.current;
    if (!section) return;

    const prefersReduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setActiveTab('oas');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      setAutoCycling(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            io.disconnect();
            const kickoff = window.setTimeout(() => setAutoCycling(true), 800);
            return () => window.clearTimeout(kickoff);
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(section);
    return () => io.disconnect();
  }, []);

  React.useEffect(() => {
    if (!autoCycling) return;
    let idx = activeTab ? TABS.findIndex((t) => t.key === activeTab) : -1;
    const step = () => {
      idx = (idx + 1) % TABS.length;
      setActiveTab(TABS[idx].key);
    };
    step();
    const timer = window.setInterval(step, 4500);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCycling]);

  const pickTab = (key: TabKey) => {
    setAutoCycling(false);
    setActiveTab(key);
  };

  const onTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, i: number) => {
    const next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : null;
    if (next === null) return;
    e.preventDefault();
    setAutoCycling(false);
    const wrapped = (next + TABS.length) % TABS.length;
    const nextKey = TABS[wrapped].key;
    setActiveTab(nextKey);
    tabsRef.current[nextKey]?.focus();
  };

  const answerWho = activeTab
    ? en
      ? TABS.find((t) => t.key === activeTab)!.label.en
      : TABS.find((t) => t.key === activeTab)!.label.nl
    : '\u00a0';

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">{en ? 'Work with us' : 'Werk met ons'}</span>
      <h2 className="site-end__title">
        {en
          ? 'A pilot starts with data, an expert a few hours a week, and a real problem.'
          : 'Een pilot start met data, een expert een paar uur per week, en een echt probleem.'}
      </h2>
      <p className="site-end__sub">
        {en
          ? "The architecture behind oasis_0 already runs in production in emergency command and in government. If your decisions have to be defended, we'd like to hear about the problem."
          : 'De architectuur achter oasis_0 draait al in productie in crisiscommando en overheid. Als je beslissingen te verdedigen moeten zijn, horen we graag het probleem.'}
      </p>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact?prefill=pilot">
          {en ? 'Talk to us' : 'Praat met ons'}
        </Link>
        <Link className="btn site-end__btn2" to="/contact?prefill=models-notify">
          {en ? 'Get notified about new models' : 'Houd me op de hoogte van nieuwe modellen'}
        </Link>
      </div>
    </div>
  );

  const descriptionMeta = en
    ? "oasis_0 — Thoughtful Oasis's first model series. What's inside every oasis model, why it can go where other AI can't, the models in the series, and how to train a custom one."
    : 'oasis_0 — de eerste modelserie van Thoughtful Oasis. Wat er in elk oasis-model zit, waar het kan waar andere AI niet komt, welke modellen in de serie zitten, en hoe je een eigen model traint.';

  return (
    <WebsiteLayout bodyClass="models" customCta={customCta}>
      <Helmet>
        <title>Models · oasis_0 · Thoughtful Oasis</title>
        <meta name="description" content={descriptionMeta} />
        <link rel="canonical" href="https://thoughtful-oasis.com/models" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Thoughtful Oasis" />
        <meta property="og:title" content="Models · oasis_0 · Thoughtful Oasis" />
        <meta
          property="og:description"
          content="oasis_0 is our first model series: specialist AI models for regulated decisions, each trained for one task, every answer traceable to named attributes."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/models" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <meta property="og:locale" content={en ? 'en_GB' : 'nl_NL'} />
      </Helmet>

      <main className="models">
        {/* Hero */}
        <header className="mhero">
          <div className="mhero__inner">
            <div className="mhero__text">
              <span className="label">
                {en ? 'Models · our first series' : 'Modellen · onze eerste serie'}
              </span>
              <h1>
                {en ? 'Introducing the oasis_0 series.' : 'Dit is de oasis_0-serie.'}
              </h1>
              <p className="mhero__sub">
                {en
                  ? 'A collection of specialist AI models for decisions that have to be defended — each trained for one regulated task, each showing the reasons behind every answer.'
                  : 'Een reeks specialistische AI-modellen voor beslissingen die je moet kunnen verdedigen: elk getraind voor één gereguleerde taak, elk met de redenen achter elk antwoord zichtbaar.'}
              </p>
              <div className="mhero__cta">
                <a className="btn btn-primary" href="#series">
                  {en ? 'The models' : 'De modellen'}
                </a>
                <a className="btn-link" href="#differs">
                  {en ? 'What makes an oasis model different' : 'Wat een oasis-model anders maakt'}
                </a>
              </div>
            </div>
            <svg
              className="mhero__sky"
              viewBox="0 0 480 480"
              aria-hidden="true"
              ref={skyRef}
            >
              <g className="sky-net">
                {CONSTELLATION_LINES.map((line, i) => {
                  const [x1, y1] = CONSTELLATION_BASE[line.a];
                  const [x2, y2] = CONSTELLATION_BASE[line.b];
                  return (
                    <line
                      key={i}
                      className={line.lit ? 'starline--lit' : 'starline'}
                      data-a={line.a}
                      data-b={line.b}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                    />
                  );
                })}
                <circle
                  data-ring="0"
                  cx={CONSTELLATION_BASE[0][0]}
                  cy={CONSTELLATION_BASE[0][1]}
                  r={12}
                  fill="none"
                  stroke="rgba(45,168,141,0.4)"
                  strokeWidth={1}
                />
                <circle
                  data-ring="1"
                  cx={CONSTELLATION_BASE[1][0]}
                  cy={CONSTELLATION_BASE[1][1]}
                  r={10}
                  fill="none"
                  stroke="rgba(196,114,78,0.45)"
                  strokeWidth={1}
                />
                <circle
                  data-ring="2"
                  cx={CONSTELLATION_BASE[2][0]}
                  cy={CONSTELLATION_BASE[2][1]}
                  r={10.5}
                  fill="none"
                  stroke="rgba(45,168,141,0.4)"
                  strokeWidth={1}
                />
                <circle className="glow" data-i="0" cx={100} cy={140} r={4.6} fill="#2DA88D" />
                <circle className="star--lit" data-i="1" cx={205} cy={230} r={4} fill="#C4724E" />
                <circle className="star--lit" data-i="2" cx={315} cy={175} r={4} fill="#2DA88D" />
                <circle className="star--dim" data-i="3" cx={150} cy={360} r={3} fill="#1A1F1B" />
                <circle className="star--dim" data-i="4" cx={400} cy={285} r={3} fill="#1A1F1B" />
                <circle className="star--dim" data-i="5" cx={430} cy={150} r={3.2} fill="#1A1F1B" />
                <circle className="star--dim" data-i="6" cx={285} cy={395} r={3} fill="#1A1F1B" />
                <circle className="star--dim" data-i="7" cx={60} cy={300} r={2.4} fill="#1A1F1B" />
              </g>
            </svg>
          </div>
        </header>

        {/* §1 Inside every oasis model */}
        <section className="section" id="what">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">
                {en ? 'What OASIS means' : 'Wat OASIS betekent'}
              </span>
              <h2>{en ? 'Inside every oasis model.' : 'In elk oasis-model.'}</h2>
              <p className="lede">
                {en
                  ? "The heart of each model is the same: the domain's real properties, written down as the dimensions of a space the model reasons in."
                  : 'De kern van elk model is hetzelfde: de echte eigenschappen van het domein, vastgelegd als de dimensies van een ruimte waarin het model redeneert.'}
              </p>
            </Reveal>
          </div>
          <Reveal className="acro">
            <div className="acro__group">
              <div>
                <div className="acro__line">
                  <span className="acro__cap">O</span>
                  <span className="acro__word">Ontological</span>
                </div>
                <div className="acro__line">
                  <span className="acro__cap">A</span>
                  <span className="acro__word">Attributes</span>
                </div>
              </div>
              <span className="acro__bracket" aria-hidden="true"></span>
              <p className="acro__comment">
                {en ? (
                  <>
                    The real, named properties of the domain — <code>building age</code>,{' '}
                    <code>people inside</code>, <code>wind speed</code> — with numeric values or
                    categorical labels. Not opaque features.
                  </>
                ) : (
                  <>
                    De echte, benoemde eigenschappen van het domein — <code>leeftijd gebouw</code>,{' '}
                    <code>mensen binnen</code>, <code>windsnelheid</code> — met numerieke waarden of
                    categorische labels. Geen ondoorzichtige features.
                  </>
                )}
              </p>
            </div>
            <div className="acro__group">
              <div>
                <div className="acro__line">
                  <span className="acro__cap">S</span>
                  <span className="acro__word">Spanning</span>
                </div>
                <div className="acro__line">
                  <span className="acro__cap">I</span>
                  <span className="acro__word">Interpretable</span>
                </div>
                <div className="acro__line">
                  <span className="acro__cap">S</span>
                  <span className="acro__word">Spaces</span>
                </div>
              </div>
              <span className="acro__bracket" aria-hidden="true"></span>
              <p className="acro__comment">
                {en
                  ? "Together, the attributes span a space that stays readable. Every case becomes a point in it, and everything the model computes — distances, regions, directions — can be read back in the domain's own terms."
                  : 'Samen spannen de attributen een ruimte op die leesbaar blijft. Elke casus wordt er een punt in, en alles wat het model berekent — afstanden, gebieden, richtingen — is terug te lezen in de begrippen van het domein zelf.'}
              </p>
            </div>
          </Reveal>
        </section>

        {/* §2 The comparison */}
        <section className="section section--dark" id="differs" ref={differsRef}>
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'The difference' : 'Het verschil'}</span>
              <h2>
                {en ? "oasis_0 can go where other AI can't." : 'oasis_0 kan waar andere AI niet komt.'}
              </h2>
            </Reveal>
          </div>

          <div className="q">
            <Reveal className="msg--user">
              <p className="msg__who">{en ? 'You' : 'Jij'}</p>
              <div className="msg__bubble">
                {en
                  ? 'A fire in an industrial hall, people possibly inside, strong wind pushing smoke toward houses. Which earlier incidents is this most like — and what does that mean for what we do?'
                  : 'Een brand in een industriehal, mogelijk mensen binnen, harde wind die de rook richting woningen duwt. Op welke eerdere incidenten lijkt dit het meest — en wat betekent dat voor wat we doen?'}
              </div>
            </Reveal>

            <p className="q__hint">
              {en
                ? 'Who answers? Pick a model to stop and compare'
                : 'Wie antwoordt? Kies een model om stil te zetten en te vergelijken'}
            </p>
            <div className="tabs" role="tablist" aria-label="Model families">
              {TABS.map((t, i) => (
                <button
                  key={t.key}
                  ref={(el) => (tabsRef.current[t.key] = el)}
                  className="tab"
                  role="tab"
                  id={`tab-${t.key}`}
                  aria-controls={`p-${t.key}`}
                  aria-selected={activeTab === t.key}
                  onClick={() => pickTab(t.key)}
                  onKeyDown={(e) => onTabKeyDown(e, i)}
                >
                  {en ? t.label.en : t.label.nl}
                </button>
              ))}
            </div>

            <div className="answer" aria-live="polite">
              <p className="msg__who" id="answer-who">
                {answerWho}
              </p>

              {/* LLM */}
              <div
                className={`panel${activeTab === 'llm' ? ' is-on' : ''}`}
                id="p-llm"
                role="tabpanel"
                aria-labelledby="tab-llm"
              >
                <div className="panel__grid">
                  <div className="out">
                    <div className="out__head">
                      <span>{en ? 'What comes back' : 'Wat je terugkrijgt'}</span>
                      <span>
                        <em>{en ? 'prose' : 'proza'}</em>
                      </span>
                    </div>
                    <div className="out__body">
                      <p className="out__prose">
                        {en
                          ? '“This resembles several past industrial fires with possible occupants. Given the smoke moving toward residential areas, consider evacuating downwind houses early and requesting additional crews. Comparable incidents typically escalated; review similar cases from recent years.”'
                          : '“Dit lijkt op meerdere eerdere industriebranden met mogelijke aanwezigen. Omdat de rook richting woonwijken trekt, overweeg de woningen benedenwinds vroeg te ontruimen en extra eenheden op te roepen. Vergelijkbare incidenten escaleerden doorgaans; bekijk soortgelijke gevallen uit recente jaren.”'}
                      </p>
                    </div>
                    <div className="out__foot">
                      {en
                        ? 'Fluent and plausible — the advice and its reasons are generated together, in one pass.'
                        : 'Vloeiend en plausibel — het advies en de redenen komen samen tot stand, in één keer.'}
                    </div>
                  </div>
                  <div className="facts">
                    <div className="fact">
                      <p className="fact__k">{en ? 'The answer is' : 'Het antwoord is'}</p>
                      <p className="fact__v">
                        {en
                          ? 'A paragraph, shaped like an argument.'
                          : 'Een alinea, gevormd als een betoog.'}
                      </p>
                    </div>
                    <div className="fact">
                      <p className="fact__k">{en ? 'You can check' : 'Je kunt controleren'}</p>
                      <p className="fact__v">
                        {en
                          ? "The wording. Which past incidents it actually drew on — and how close they really are — isn't recorded anywhere."
                          : 'De formulering. Op welke eerdere incidenten het zich baseerde — en hoe dichtbij die echt zijn — staat nergens vastgelegd.'}
                      </p>
                    </div>
                    <div className="fact">
                      <p className="fact__k">{en ? 'Good at' : 'Goed in'}</p>
                      <p className="fact__v">
                        {en
                          ? 'Drafting, summarising, conversation. Genuinely.'
                          : 'Concepten schrijven, samenvatten, gesprek. Echt waar.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Embedding search */}
              <div
                className={`panel${activeTab === 'emb' ? ' is-on' : ''}`}
                id="p-emb"
                role="tabpanel"
                aria-labelledby="tab-emb"
              >
                <div className="panel__grid">
                  <div className="out">
                    <div className="out__head">
                      <span>{en ? 'What comes back' : 'Wat je terugkrijgt'}</span>
                      <span>
                        <em>{en ? 'ranked list' : 'gerangschikte lijst'}</em>
                      </span>
                    </div>
                    <div className="out__body">
                      <pre className="out__mono">
{`1.  incident 2023-114   similarity 0.87
2.  incident 2021-032   similarity 0.84
3.  incident 2024-201   similarity 0.82
`}
                        <span className="out__dim">
                          {en
                            ? '    why 0.87 and not 0.61: one number over hundreds of unnamed dimensions'
                            : '    waarom 0.87 en niet 0.61: één getal over honderden naamloze dimensies'}
                        </span>
                      </pre>
                    </div>
                    <div className="out__foot">
                      {en
                        ? "Fast and stable. But a list is not advice — and the reason for the ranking can't be read out."
                        : 'Snel en stabiel. Maar een lijst is geen advies — en de reden voor de rangschikking is niet af te lezen.'}
                    </div>
                  </div>
                  <div className="facts">
                    <div className="fact">
                      <p className="fact__k">{en ? 'The answer is' : 'Het antwoord is'}</p>
                      <p className="fact__v">
                        {en
                          ? 'A list of neighbours, one similarity score each.'
                          : 'Een lijst buren, elk met één gelijkenisscore.'}
                      </p>
                    </div>
                    <div className="fact">
                      <p className="fact__k">{en ? 'You can check' : 'Je kunt controleren'}</p>
                      <p className="fact__v">
                        {en
                          ? 'A single distance. Not whether the match is close on the building but wrong about the wind.'
                          : 'Eén afstand. Niet of de match klopt op het gebouw maar de wind mist.'}
                      </p>
                    </div>
                    <div className="fact">
                      <p className="fact__k">{en ? 'Good at' : 'Goed in'}</p>
                      <p className="fact__v">
                        {en
                          ? 'Finding related text at scale — recall, not reasoning.'
                          : 'Op schaal verwante tekst vinden — vindbaarheid, geen redenering.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Classifier */}
              <div
                className={`panel${activeTab === 'clf' ? ' is-on' : ''}`}
                id="p-clf"
                role="tabpanel"
                aria-labelledby="tab-clf"
              >
                <div className="panel__grid">
                  <div className="out">
                    <div className="out__head">
                      <span>{en ? 'What comes back' : 'Wat je terugkrijgt'}</span>
                      <span>
                        <em>label</em>
                      </span>
                    </div>
                    <div className="out__body">
                      <pre className="out__mono">
{`label       SCALE_UP
confidence  0.91
`}
                        <span className="out__dim">
                          {en
                            ? 'features    f_412 (+0.22) · f_77 (−0.14) · f_1903 (+0.09)\n            internal features, not situation attributes'
                            : 'features    f_412 (+0.22) · f_77 (−0.14) · f_1903 (+0.09)\n            interne features, geen situatie-attributen'}
                        </span>
                      </pre>
                    </div>
                    <div className="out__foot">
                      {en
                        ? 'A verdict without a language for it. The features belong to the model, not the domain.'
                        : 'Een oordeel zonder taal erbij. De features horen bij het model, niet bij het domein.'}
                    </div>
                  </div>
                  <div className="facts">
                    <div className="fact">
                      <p className="fact__k">{en ? 'The answer is' : 'Het antwoord is'}</p>
                      <p className="fact__v">
                        {en
                          ? 'A category and a confidence number.'
                          : 'Een categorie en een zekerheidsgetal.'}
                      </p>
                    </div>
                    <div className="fact">
                      <p className="fact__k">{en ? 'You can check' : 'Je kunt controleren'}</p>
                      <p className="fact__v">
                        {en
                          ? 'A post-hoc attribution over internal features, at best — not what the incident evaluation will ask about.'
                          : 'Hooguit een achteraf-toewijzing over interne features — niet waar de incidentevaluatie naar vraagt.'}
                      </p>
                    </div>
                    <div className="fact">
                      <p className="fact__k">{en ? 'Good at' : 'Goed in'}</p>
                      <p className="fact__v">
                        {en
                          ? 'High-volume, repetitive decisions where the label is the whole result.'
                          : 'Grote aantallen, repetitieve beslissingen waar het label het hele resultaat is.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* oasis_0 */}
              <div
                className={`panel${activeTab === 'oas' ? ' is-on' : ''}`}
                id="p-oas"
                role="tabpanel"
                aria-labelledby="tab-oas"
              >
                <div className="panel__grid">
                  <div className="out">
                    <div className="out__head">
                      <span>{en ? 'What comes back' : 'Wat je terugkrijgt'}</span>
                      <span>
                        <em>
                          {en ? 'attributes → distance → advice' : 'attributen → afstand → advies'}
                        </em>
                      </span>
                    </div>
                    <div className="out__body">
                      <div className="attr">
                        <div className="attr__hd">
                          <span>{en ? 'Attribute' : 'Attribuut'}</span>
                          <span>{en ? 'This incident' : 'Dit incident'}</span>
                          <span>Incident 2023-114</span>
                          <span>Δ</span>
                        </div>
                        <div className="attr__row">
                          <span className="attr__name">
                            {en ? 'building type' : 'bouwtype'}
                          </span>
                          <span className="attr__val">
                            {en ? 'industrial hall' : 'industriehal'}
                          </span>
                          <span className="attr__val">
                            {en ? 'industrial hall' : 'industriehal'}
                          </span>
                          <span className="delta delta--near">0.00</span>
                        </div>
                        <div className="attr__row">
                          <span className="attr__name">
                            {en ? 'building age' : 'leeftijd gebouw'}
                          </span>
                          <span className="attr__val">1994</span>
                          <span className="attr__val">1989</span>
                          <span className="delta delta--near">0.03</span>
                        </div>
                        <div className="attr__row">
                          <span className="attr__name">
                            {en ? 'people inside' : 'mensen binnen'}
                          </span>
                          <span className="attr__val">
                            {en ? 'est. 2–5' : 'gesch. 2–5'}
                          </span>
                          <span className="attr__val">3</span>
                          <span className="delta delta--near">0.04</span>
                        </div>
                        <div className="attr__row">
                          <span className="attr__name">
                            {en ? 'wind speed' : 'windsnelheid'}
                          </span>
                          <span className="attr__val">
                            {en ? '14 m/s, toward houses' : '14 m/s, richting woningen'}
                          </span>
                          <span className="attr__val">
                            {en ? '5 m/s, toward houses' : '5 m/s, richting woningen'}
                          </span>
                          <span className="delta delta--far">0.58</span>
                        </div>
                        <div className="attr__row">
                          <span className="attr__name">
                            {en ? 'hazardous materials' : 'gevaarlijke stoffen'}
                          </span>
                          <span className="attr__val">{en ? 'unknown' : 'onbekend'}</span>
                          <span className="attr__val">
                            {en ? 'solvents' : 'oplosmiddelen'}
                          </span>
                          <span className="delta delta--unk">?</span>
                        </div>
                      </div>
                    </div>
                    <div className="out__foot">
                      {en ? (
                        <>
                          Closest case: <strong>incident 2023-114</strong> · overall distance 0.21
                          <br />
                          <span className="adv">
                            → Same situation as 2023-114 on nearly every attribute — but wind speed
                            is far higher, so the advised course shifts from an interior attack to a
                            defensive line with early evacuation of the downwind houses. Hazardous
                            materials are still unknown: flagged, not assumed.
                          </span>
                        </>
                      ) : (
                        <>
                          Dichtstbijzijnde casus: <strong>incident 2023-114</strong> · totale
                          afstand 0.21
                          <br />
                          <span className="adv">
                            → Dezelfde situatie als 2023-114 op vrijwel elk attribuut — maar de
                            windsnelheid ligt veel hoger, dus het advies verschuift van een
                            binneninzet naar een defensieve lijn met vroege ontruiming van de
                            woningen benedenwinds. Gevaarlijke stoffen zijn nog onbekend:
                            gemarkeerd, niet aangenomen.
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="facts">
                    <div className="fact">
                      <p className="fact__k">{en ? 'The answer is' : 'Het antwoord is'}</p>
                      <p className="fact__v">
                        {en
                          ? 'The nearest historic cases — as distances over named attributes with real values, not one opaque score — and advice synthesized from exactly that comparison.'
                          : 'De dichtstbijzijnde historische casussen — als afstanden over benoemde attributen met echte waarden, niet één ondoorzichtige score — en advies dat precies uit die vergelijking is opgebouwd.'}
                      </p>
                    </div>
                    <div className="fact">
                      <p className="fact__k">{en ? 'You can check' : 'Je kunt controleren'}</p>
                      <p className="fact__v">
                        {en
                          ? "Every attribute's value on both sides, which difference moved the advice, and what is still unknown."
                          : 'De waarde van elk attribuut aan beide kanten, welk verschil het advies stuurde, en wat nog onbekend is.'}
                      </p>
                    </div>
                    <div className="fact">
                      <p className="fact__k">{en ? 'Good at' : 'Goed in'}</p>
                      <p className="fact__v">
                        {en
                          ? 'Decisions someone has to explain afterwards — to an evaluation, a regulator, a court.'
                          : 'Beslissingen die iemand achteraf moet uitleggen — aan een evaluatie, een toezichthouder, een rechter.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p className="q__cap">
              {en ? 'Illustrative outputs.' : 'Illustratieve uitvoer.'}
            </p>
          </div>
        </section>

        {/* §3 The models */}
        <section className="section" id="series">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'Models' : 'Modellen'}</span>
              <h2>{en ? 'Our models.' : 'Onze modellen.'}</h2>
            </Reveal>
            <div className="legend">
              <span>
                <span className="dot dot--on"></span>
                {en ? 'In production' : 'In productie'}
              </span>
              <span>
                <span className="dot dot--next"></span>
                {en ? 'Early access' : 'Vroege toegang'}
              </span>
              <span>
                <span className="dot dot--off"></span>
                {en ? 'Coming soon' : 'Binnenkort'}
              </span>
            </div>

            <div className="clusters">
              {/* Crisis management */}
              <Reveal className="cluster cluster--live">
                <div className="cluster__head">
                  <span className="cluster__name">
                    {en ? 'Crisis management' : 'Crisisbeheersing'}
                  </span>
                  <span className="cluster__meta">
                    {en ? '1 model · in production' : '1 model · in productie'}
                  </span>
                </div>
                <div className="mrow">
                  <span className="mrow__mark mrow__mark--on"></span>
                  <div>
                    <span className="mrow__name">oasis_0 · Prometheus</span>
                    <div className="mrow__desc">
                      {en
                        ? 'Decision-support for firefighting · powers MissionOS'
                        : 'Beslissingsondersteuning voor brandbestrijding · draait onder MissionOS'}
                    </div>
                  </div>
                  <span className="mrow__act">
                    <Link to="/mission-os">
                      {en ? 'View model →' : 'Bekijk model →'}
                    </Link>
                  </span>
                </div>
              </Reveal>

              {/* Medical */}
              <Reveal className="cluster cluster--next">
                <div className="cluster__head">
                  <span className="cluster__name">{en ? 'Medical' : 'Medisch'}</span>
                  <span className="cluster__meta">
                    {en ? '2 models · 1 in early access' : '2 modellen · 1 in vroege toegang'}
                  </span>
                </div>
                <div className="mrow">
                  <span className="mrow__mark mrow__mark--next"></span>
                  <div>
                    <span className="mrow__name">oasis_0 · Asclepius 510k</span>
                    <div className="mrow__desc">
                      {en
                        ? 'FDA 510(k) predicate search & substantial-equivalence preparation'
                        : 'FDA 510(k) predicate-search & voorbereiding substantiële gelijkwaardigheid'}
                    </div>
                  </div>
                  <span className="mrow__act">
                    <Link to="/models/asclepius-510k">
                      {en ? 'View model →' : 'Bekijk model →'}
                    </Link>
                  </span>
                </div>
                <button
                  className="more"
                  type="button"
                  aria-expanded={!!openClusters.medical}
                  onClick={() => toggleCluster('medical')}
                >
                  <span>
                    {en ? '+ 1 model coming soon' : '+ 1 model binnenkort'}
                  </span>
                  <span className="more__chev">{openClusters.medical ? '−' : '+'}</span>
                </button>
                <div className="more__body" hidden={!openClusters.medical}>
                  <div className="mrow">
                    <span className="mrow__mark mrow__mark--off"></span>
                    <div>
                      <span className="mrow__name">oasis_0 · Asclepius Eligibility</span>
                      <div className="mrow__desc">
                        {en
                          ? 'Clinical-trial eligibility matching'
                          : 'Matching van geschiktheid voor klinische studies'}
                      </div>
                    </div>
                    <span className="mrow__act">
                      <Link className="notify" to="/contact?prefill=models-notify">
                        {en ? 'Get notified →' : 'Houd me op de hoogte →'}
                      </Link>
                    </span>
                  </div>
                </div>
              </Reveal>

              {/* Compliance & accountability */}
              <Reveal className="cluster cluster--live">
                <div className="cluster__head">
                  <span className="cluster__name">
                    {en ? 'Compliance & accountability' : 'Compliance & verantwoording'}
                  </span>
                  <span className="cluster__meta">
                    {en ? '4 models · 1 in production' : '4 modellen · 1 in productie'}
                  </span>
                </div>
                <div className="mrow">
                  <span className="mrow__mark mrow__mark--on"></span>
                  <div>
                    <span className="mrow__name">oasis_0 · Themis</span>
                    <div className="mrow__desc">
                      {en
                        ? 'Answering government information requests (FOI / Woo) · powers VraagMijnOverheid'
                        : 'Beantwoorden van overheidsinformatieverzoeken (Woo) · draait onder VraagMijnOverheid'}
                    </div>
                  </div>
                  <span className="mrow__act">
                    <Link to="/vmo">{en ? 'View model →' : 'Bekijk model →'}</Link>
                  </span>
                </div>
                <button
                  className="more"
                  type="button"
                  aria-expanded={!!openClusters.compliance}
                  onClick={() => toggleCluster('compliance')}
                >
                  <span>
                    {en ? '+ 3 models coming soon' : '+ 3 modellen binnenkort'}
                  </span>
                  <span className="more__chev">{openClusters.compliance ? '−' : '+'}</span>
                </button>
                <div className="more__body" hidden={!openClusters.compliance}>
                  <div className="mrow">
                    <span className="mrow__mark mrow__mark--off"></span>
                    <div>
                      <span className="mrow__name mrow__name--muted">
                        oasis_0 ————— · · ·
                      </span>
                      <div className="mrow__desc mrow__desc--muted">
                        {en
                          ? 'Mapping obligations, standards, and controls — with a verifiable trail'
                          : 'Verplichtingen, normen en maatregelen in kaart brengen — met een controleerbaar spoor'}
                      </div>
                    </div>
                    <span className="mrow__act">
                      <Link className="notify" to="/contact?prefill=models-notify">
                        {en ? 'Get notified →' : 'Houd me op de hoogte →'}
                      </Link>
                    </span>
                  </div>
                  <div className="mrow">
                    <span className="mrow__mark mrow__mark--off"></span>
                    <div>
                      <span className="mrow__name mrow__name--muted">
                        oasis_0 ————— · · ·
                      </span>
                      <div className="mrow__desc mrow__desc--muted">
                        {en
                          ? 'Conformity assessment against required standards'
                          : 'Conformiteitsbeoordeling tegen vereiste normen'}
                      </div>
                    </div>
                    <span className="mrow__act">
                      <Link className="notify" to="/contact?prefill=models-notify">
                        {en ? 'Get notified →' : 'Houd me op de hoogte →'}
                      </Link>
                    </span>
                  </div>
                  <div className="mrow">
                    <span className="mrow__mark mrow__mark--off"></span>
                    <div>
                      <span className="mrow__name mrow__name--muted">
                        oasis_0 ————— · · ·
                      </span>
                      <div className="mrow__desc mrow__desc--muted">
                        {en
                          ? 'Tender evaluation on named award criteria'
                          : 'Aanbestedingsbeoordeling op benoemde gunningscriteria'}
                      </div>
                    </div>
                    <span className="mrow__act">
                      <Link className="notify" to="/contact?prefill=models-notify">
                        {en ? 'Get notified →' : 'Houd me op de hoogte →'}
                      </Link>
                    </span>
                  </div>
                </div>
              </Reveal>

              {/* Industrial */}
              <Reveal className="cluster cluster--locked">
                <div className="cluster__head">
                  <span className="cluster__name" style={{ color: 'var(--ink-soft)' }}>
                    {en ? 'Industrial' : 'Industrie'}
                  </span>
                  <span className="cluster__meta">
                    {en ? '1 model · in development' : '1 model · in ontwikkeling'}
                  </span>
                </div>
                <button
                  className="more"
                  type="button"
                  aria-expanded={!!openClusters.industrial}
                  onClick={() => toggleCluster('industrial')}
                >
                  <span>
                    {en ? '+ 1 model coming soon' : '+ 1 model binnenkort'}
                  </span>
                  <span className="more__chev">{openClusters.industrial ? '−' : '+'}</span>
                </button>
                <div className="more__body" hidden={!openClusters.industrial}>
                  <div className="mrow">
                    <span className="mrow__mark mrow__mark--off"></span>
                    <div>
                      <span className="mrow__name mrow__name--muted">
                        oasis_0 ————— · · ·
                      </span>
                      <div className="mrow__desc mrow__desc--muted">
                        {en ? 'Component management' : 'Componentbeheer'}
                      </div>
                    </div>
                    <span className="mrow__act">
                      <Link className="notify" to="/contact?prefill=models-notify">
                        {en ? 'Get notified →' : 'Houd me op de hoogte →'}
                      </Link>
                    </span>
                  </div>
                </div>
              </Reveal>

              {/* Discovery */}
              <Reveal className="cluster cluster--locked">
                <div className="cluster__head">
                  <span className="cluster__name" style={{ color: 'var(--ink-soft)' }}>
                    {en ? 'Discovery' : 'Ontdekking'}
                  </span>
                  <span className="cluster__meta">
                    {en ? '2 models · in development' : '2 modellen · in ontwikkeling'}
                  </span>
                </div>
                <button
                  className="more"
                  type="button"
                  aria-expanded={!!openClusters.discovery}
                  onClick={() => toggleCluster('discovery')}
                >
                  <span>
                    {en ? '+ 2 models coming soon' : '+ 2 modellen binnenkort'}
                  </span>
                  <span className="more__chev">{openClusters.discovery ? '−' : '+'}</span>
                </button>
                <div className="more__body" hidden={!openClusters.discovery}>
                  <div className="mrow">
                    <span className="mrow__mark mrow__mark--off"></span>
                    <div>
                      <span className="mrow__name mrow__name--muted">
                        oasis_0 ————— · · ·
                      </span>
                      <div className="mrow__desc mrow__desc--muted">
                        {en
                          ? 'Enzyme and strain selection'
                          : 'Enzym- en stamselectie'}
                      </div>
                    </div>
                    <span className="mrow__act">
                      <Link className="notify" to="/contact?prefill=models-notify">
                        {en ? 'Get notified →' : 'Houd me op de hoogte →'}
                      </Link>
                    </span>
                  </div>
                  <div className="mrow">
                    <span className="mrow__mark mrow__mark--off"></span>
                    <div>
                      <span className="mrow__name mrow__name--muted">
                        oasis_0 ————— · · ·
                      </span>
                      <div className="mrow__desc mrow__desc--muted">
                        {en ? 'Materials inverse design' : 'Inverse materiaalontwerp'}
                      </div>
                    </div>
                    <span className="mrow__act">
                      <Link className="notify" to="/contact?prefill=models-notify">
                        {en ? 'Get notified →' : 'Houd me op de hoogte →'}
                      </Link>
                    </span>
                  </div>
                </div>
              </Reveal>

              {/* Research & knowledge */}
              <Reveal className="cluster cluster--locked">
                <div className="cluster__head">
                  <span className="cluster__name" style={{ color: 'var(--ink-soft)' }}>
                    {en ? 'Research & knowledge' : 'Onderzoek & kennis'}
                  </span>
                  <span className="cluster__meta">
                    {en ? '1 model · in development' : '1 model · in ontwikkeling'}
                  </span>
                </div>
                <button
                  className="more"
                  type="button"
                  aria-expanded={!!openClusters.research}
                  onClick={() => toggleCluster('research')}
                >
                  <span>
                    {en ? '+ 1 model coming soon' : '+ 1 model binnenkort'}
                  </span>
                  <span className="more__chev">{openClusters.research ? '−' : '+'}</span>
                </button>
                <div className="more__body" hidden={!openClusters.research}>
                  <div className="mrow">
                    <span className="mrow__mark mrow__mark--off"></span>
                    <div>
                      <span className="mrow__name mrow__name--muted">
                        oasis_0 ————— · · ·
                      </span>
                      <div className="mrow__desc mrow__desc--muted">
                        {en
                          ? 'Automatic taxonomy induction for knowledge and innovation domains'
                          : 'Automatische taxonomie-inductie voor kennis- en innovatiedomeinen'}
                      </div>
                    </div>
                    <span className="mrow__act">
                      <Link className="notify" to="/contact?prefill=models-notify">
                        {en ? 'Get notified →' : 'Houd me op de hoogte →'}
                      </Link>
                    </span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* §4 Architecture */}
        <section className="section section--paper" id="architecture">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'Architecture' : 'Architectuur'}</span>
              <h2>
                {en
                  ? 'Three building blocks that make intelligence auditable.'
                  : 'Drie bouwstenen die intelligentie controleerbaar maken.'}
              </h2>
              <p className="lede">
                {en
                  ? 'Every oasis_0 model is built from the same three blocks. Reasoning only ever happens on named attributes — so auditability is built in, not added afterwards.'
                  : 'Elk oasis_0-model is opgebouwd uit dezelfde drie blokken. Redeneren gebeurt altijd op benoemde attributen — zodat controleerbaarheid ingebouwd is, niet achteraf toegevoegd.'}
              </p>
            </Reveal>
          </div>

          <div className="arch">
            <Reveal className="arch__step">
              <p className="arch__num">01 · Encoders</p>
              <p className="arch__title">
                {en ? 'Read input into the space.' : 'Invoer inlezen in de ruimte.'}
              </p>
              <p className="arch__body">
                {en
                  ? 'Modified small language models embed arbitrary input — text, records, sensor traces — into the named dimensions. Deterministic encoders handle structured tabular data.'
                  : 'Aangepaste kleine taalmodellen plaatsen willekeurige invoer — tekst, dossiers, sensormetingen — in de benoemde dimensies. Deterministische encoders verwerken gestructureerde tabeldata.'}
              </p>
            </Reveal>
            <div className="arch__arrow">→</div>
            <Reveal className="arch__step">
              <p className="arch__num">
                {en ? '02 · Vector ontology' : '02 · Vector-ontologie'}
              </p>
              <p className="arch__title">
                {en ? 'The named space itself.' : 'De benoemde ruimte zelf.'}
              </p>
              <p className="arch__body">
                {en
                  ? 'A learned space whose axes are named domain attributes. Situations, cases and answers all become points in it.'
                  : 'Een geleerde ruimte met benoemde domeinattributen als assen. Situaties, casussen en antwoorden worden er allemaal punten in.'}
              </p>
            </Reveal>
            <div className="arch__arrow">→</div>
            <Reveal className="arch__step">
              <p className="arch__num">
                {en ? '03 · Reasoning engines' : '03 · Redeneermotoren'}
              </p>
              <p className="arch__title">
                {en ? 'Compute on the space.' : 'Rekenen op de ruimte.'}
              </p>
              <p className="arch__body">
                {en
                  ? 'Hand-designed geometric algorithms. Every output is attributable to the dimensions that produced it — that is where the audit trail comes from.'
                  : 'Met de hand ontworpen geometrische algoritmen. Elke uitkomst is te herleiden tot de dimensies die haar voortbrachten — daar komt het controlespoor vandaan.'}
              </p>
            </Reveal>
          </div>

          <Reveal className="opstrip">
            <div className="opstrip__cell">
              <p className="opstrip__k">{en ? 'Search' : 'Zoeken'}</p>
              <p className="opstrip__v">
                {en
                  ? 'Find what satisfies named constraints, with the reasons per result.'
                  : 'Vind wat aan benoemde randvoorwaarden voldoet, met de redenen per resultaat.'}
              </p>
            </div>
            <div className="opstrip__cell">
              <p className="opstrip__k">
                {en ? 'Classification' : 'Classificatie'}
              </p>
              <p className="opstrip__v">
                {en
                  ? 'Place a case in a category, and say what placed it.'
                  : "Plaats een casus in een categorie, en benoem wat 'm daar plaatste."}
              </p>
            </div>
            <div className="opstrip__cell">
              <p className="opstrip__k">{en ? 'Prediction' : 'Voorspelling'}</p>
              <p className="opstrip__v">
                {en
                  ? 'Estimate how a situation develops, from where it sits in the space.'
                  : 'Schat in hoe een situatie zich ontwikkelt, vanuit waar die in de ruimte ligt.'}
              </p>
            </div>
            <div className="opstrip__cell">
              <p className="opstrip__k">{en ? 'Planning' : 'Planning'}</p>
              <p className="opstrip__v">
                {en
                  ? 'Choose actions that move a situation toward a target, under constraints.'
                  : 'Kies acties die een situatie naar een doel bewegen, binnen randvoorwaarden.'}
              </p>
            </div>
          </Reveal>
        </section>

        {/* §5 Custom models */}
        <section className="section" id="custom">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'Custom models' : 'Eigen modellen'}</span>
              <h2>
                {en
                  ? 'Train your own model, or fine-tune one of ours.'
                  : 'Train je eigen model, of stem er een van ons af.'}
              </h2>
              <p className="lede">
                {en
                  ? "The series is pretrained; the architecture isn't limited to it."
                  : 'De serie is voorgetraind; de architectuur is er niet toe beperkt.'}
              </p>
            </Reveal>
          </div>
          <div className="deploy-grid">
            <Reveal className="deploy-grid__cell">
              <span className="label">
                {en ? 'Fine-tune a series model' : 'Een seriemodel fine-tunen'}
              </span>
              <p>
                {en
                  ? 'Every pretrained oasis_0 model can be fine-tuned on your own data — your case history, your records — while staying fully readable.'
                  : 'Elk voorgetraind oasis_0-model kun je fine-tunen op je eigen data — je casushistorie, je dossiers — terwijl het volledig leesbaar blijft.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">
                {en ? 'Train from scratch' : 'Vanaf nul trainen'}
              </span>
              <p>
                {en
                  ? 'Point the architecture at your documents, records or sensor data and the ontology trains itself — no labelling, no feature engineering.'
                  : 'Richt de architectuur op je documenten, dossiers of sensordata en de ontologie traint zichzelf — geen labelen, geen feature-engineering.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">
                {en ? 'Your experts review it' : 'Jouw experts toetsen het'}
              </span>
              <p>
                {en
                  ? 'The system proposes the named attributes from your data; a domain expert verifies and adjusts them. Hours of expert time, not months.'
                  : 'Het systeem stelt de benoemde attributen voor uit je data; een domeinexpert controleert en past ze aan. Uren experttijd, geen maanden.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">
                {en ? 'Versioned like code' : 'Geversioneerd als code'}
              </span>
              <p>
                {en
                  ? 'Each training run produces a versioned ontology. Compare, roll back, or branch — and challenge any attribute before it reaches production.'
                  : 'Elke trainingsrun levert een geversioneerde ontologie. Vergelijk, draai terug of vertak — en betwist elk attribuut voordat het in productie komt.'}
              </p>
            </Reveal>
          </div>
        </section>

        {/* §6 Deployment */}
        <section className="section section--paper" id="deployment">
          <div className="container">
            <Reveal className="section-head">
              <span className="label">{en ? 'Deployment' : 'Uitrol'}</span>
              <h2>
                {en
                  ? 'EU-hosted or on-premise, auditable end to end.'
                  : 'In de EU gehost of on-premise, controleerbaar van begin tot eind.'}
              </h2>
              <p className="lede">
                {en
                  ? 'API-first, with role-scoped access and an immutable trace from query to result.'
                  : 'API-first, met toegang per rol en een onveranderbaar spoor van vraag tot resultaat.'}
              </p>
            </Reveal>
          </div>
          <div className="deploy-grid">
            <Reveal className="deploy-grid__cell">
              <span className="label">{en ? 'Hosting' : 'Hosting'}</span>
              <p>
                {en
                  ? 'EU-hosted by default. On-premise where data cannot leave the perimeter: security authorities, ministries, clinical institutions.'
                  : 'Standaard in de EU gehost. On-premise waar data de perimeter niet mag verlaten: veiligheidsdiensten, ministeries, klinische instellingen.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">{en ? 'Versioning' : 'Versionering'}</span>
              <p>
                {en
                  ? 'Every answer references the ontology version that produced it. Audits replay against the version that was live at the time.'
                  : 'Elk antwoord verwijst naar de ontologieversie die het voortbracht. Audits spelen af tegen de versie die op dat moment live was.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">{en ? 'Access' : 'Toegang'}</span>
              <p>
                {en
                  ? 'Experts edit attributes through an admin surface; engineers integrate via the API; auditors see the immutable trace.'
                  : 'Experts bewerken attributen via een beheerscherm; engineers integreren via de API; auditors zien het onveranderbare spoor.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">
                {en ? 'Observability' : 'Observeerbaarheid'}
              </span>
              <p>
                {en
                  ? 'Logs, metrics and tracing in OpenTelemetry format. Every call carries the ontology version and the attributes that drove the result.'
                  : 'Logs, metrics en tracing in OpenTelemetry-formaat. Elke aanroep draagt de ontologieversie en de attributen die het resultaat stuurden.'}
              </p>
            </Reveal>
          </div>
          <p
            className="body-text"
            style={{
              margin: '52px auto 0',
              maxWidth: 'var(--max-w-prose)',
              textAlign: 'center',
              padding: '0 var(--pad-x)',
            }}
          >
            {en ? (
              <>
                Sub-processors, certifications, incident response and the full data-handling model:{' '}
                <Link className="link-inline" to="/trust">
                  Trust &amp; compliance
                </Link>
                .
              </>
            ) : (
              <>
                Sub-verwerkers, certificeringen, incidentrespons en het volledige model voor
                gegevensverwerking:{' '}
                <Link className="link-inline" to="/trust">
                  Vertrouwen &amp; compliance
                </Link>
                .
              </>
            )}
          </p>
        </section>
      </main>
    </WebsiteLayout>
  );
};

export default Models;
